import { SelectionModel } from '@angular/cdk/collections';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CalAngularService } from '@cvx/cal-angular';
import { PageLayout } from '@cvx/nextpage';
import { IGenericApiResponseWithWorkflowRequest } from 'src/app/models/common/GenericApiResponseWithWorkflowRequest';
import { ResultSizeConstants } from 'src/app/models/common/enums/ResultSizeConstants.enum';
import { UserType } from 'src/app/models/directory/enums/UserType.enum';
import { IDirectoryUser } from 'src/app/models/directory/users/DirectoryUser';
import { IWorkflowRequest } from 'src/app/models/requests/WorkflowRequest';
import { GraphUserService } from 'src/app/services/graph/GraphUser.service';
import Swal from 'sweetalert2';
import { matTableSelectionHelper } from 'src/app/helpers/matTableSelectionHelper';
import { UntypedFormControl, Validators } from '@angular/forms';
import { DeleteUserRequest } from 'src/app/models/DeleteUserRequest';
import { FormService } from "src/app/services/form.service";
import { GraphConstants } from 'src/app/models/graph/Graph.constants';
import { ChevronBrandColors } from 'src/app/helpers/brand/chevronBrandColors.enum';

@Component({
  selector: 'app-delete-user',
  templateUrl: './delete-user.component.html',
  styleUrls: ['./delete-user.component.css']
})
export class DeleteUserComponent implements OnInit {
  @ViewChild(MatPaginator, { static: false })
  set paginator(value: MatPaginator) {
    this.usersDataSource.paginator = value;
  }
  @ViewChild(MatSort, { static: false })
  set sort(value: MatSort) {
    this.usersDataSource.sort = value;
  }

  @Input() userTypeToggleOptions: UserType[] = [];
  @Input() userType: UserType | undefined;
  @Input() displayColumns: string[] = [];
  @Input() route: string;

  //for toggle use
  readonly adminRole: string = 'support';
  readonly userTypesThatSupportUsingAdminRole: UserType[] = [UserType.Service, UserType.Guest];
  adminToggleOptions: string[];
  defaultRole: string = 'steward';
  showAdminToggleOptions: boolean = false;
  usingAdminRole: boolean = false;

  title: string = 'account type';
  initialSelection: string = UserType.Service;

  userTypeString: string | undefined;
  selectedDisplayCols: string[];
  cols: string[]
  PageLayout = PageLayout;
  errorMessage: string;
  gettingUsers: boolean = true;
  isSubmitting: boolean = false;
  submittedRequests: IWorkflowRequest[] = [];
  submitErrorMessage = { message: '', errors: [] };
  selection = new SelectionModel<IDirectoryUser>(true, []);
  retireResults: string[] = [];
  usersDataSource = new MatTableDataSource<IDirectoryUser>([]);
  usersDataSourceAsSteward = new MatTableDataSource<IDirectoryUser>([]);
  usersDataSourceAsSupport = new MatTableDataSource<IDirectoryUser>([]);

  showSelectAllCheckbox: boolean = false;
  hasAdminRole: boolean = false;

  justification = new UntypedFormControl('', { validators: [Validators.required] });

  alertOnpremUserTypes: UserType[] = [UserType.Service, UserType.Privilege];

  constructor(
    private userService: GraphUserService,
    private authService: CalAngularService,
    private readonly formService: FormService
  ) { }

  async ngOnInit(): Promise<void> {
    this.cols = ['select', ...this.displayColumns];
    this.selectedDisplayCols = [...this.displayColumns];
    this.usersDataSource.paginator = this.paginator
    this.usersDataSource.sort = this.sort

    if (this.userType === UserType.Coco) {
      this.usersDataSource.filterPredicate = (user: IDirectoryUser, filter: string) => {
        const searchValue = filter.trim().toLowerCase();
        return (user.displayName ?? '').toLowerCase().includes(searchValue)
          || (user.mail ?? '').toLowerCase().includes(searchValue)
          || (user.department ?? '').toLowerCase().includes(searchValue)
          || (user.employeeId ?? '').toLowerCase().includes(searchValue);
      };
    }

    if (this.userType === UserType.Guest) {
      this.defaultRole = "sponsor";
    }
    this.adminToggleOptions = [this.defaultRole, this.adminRole]
    this.showAdminToggleOptions = this.userTypesThatSupportUsingAdminRole.some(x => x == this.userType);
    // check if the user has User.ReadWrite.All? if so show search all section on UI
    this.hasAdminRole = this.userService.CheckIsUserNonPrimaryUserAdmin(this.authService.cvxClaimsPrincipal);

    this.search();
  }

  hasPermissions(): boolean {
    if (this.userType == UserType.Coco) {
      return this.userService.CheckCocoUserSponsorshipPermission(this.authService.cvxClaimsPrincipal);
    }
    return true;
  }

  select(row: IDirectoryUser) {
    this.selection.toggle(row);
  }

  public filter = (value: any) => {
    const filterValue = typeof value === 'string' ? value : value.target.value;
    this.usersDataSource.filter = filterValue.trim().toLocaleLowerCase();
  }

  async onSubmit() {

    let userIds = this.selection.selected.map(s => s.id);
    let deleteUserRequest = new DeleteUserRequest(userIds, this.usingAdminRole, this.justification.value);

    if (userIds.length === 0 || (this.usingAdminRole && !this.justification.valid)) {
      this.formService.setIsValidateUpdateUserForms(true);
      return;
    }

    // clear error messages
    this.submitErrorMessage.message = '';
    this.submitErrorMessage.errors = [];

    if (this.selection.selected.length > 0) {

      let messageText = `${this.userTypeString} accounts`;

      if (this.selection.selected.length === 1) {
        messageText = `${this.userTypeString} account`;
      }

      Swal.fire({
        icon: 'warning',
        iconColor: ChevronBrandColors.MediumRed,
        title: '<h3>are you sure?</h3>',
        html: this.getDeleteConfirmationHtml(messageText),
        showCancelButton: true,
        confirmButtonText: 'confirm',
        cancelButtonText: 'cancel',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'button',
          cancelButton: 'button',
        },
      }).then((result: any) => {
        if (result.isConfirmed) {
          this.retireAccounts(deleteUserRequest);
        }
      });
    }
  }

  private getDeleteConfirmationHtml(messageText: string): string {
    const baseMessage = `<p>You are about to delete ${this.selection.selected.length} ${messageText}, this operation can't be undone.</p>`;

    if (this.userType !== UserType.Coco) {
      return baseMessage;
    }

    const rows = this.selection.selected.map(user => {
      const displayName = this.getUserDisplayValue(user, ['displayName', 'name', 'userPrincipalName']);
      const email = this.getUserDisplayValue(user, ['mail', 'email', 'userPrincipalName']);
      const employeeId = this.getUserDisplayValue(user, ['employeeId', 'employeeID']);

      return `
      <tr>
        <td style="text-align:left; padding: 4px 8px;">${displayName}</td>
        <td style="text-align:left; padding: 4px 8px;">${email}</td>
        <td style="text-align:left; padding: 4px 8px; white-space: nowrap;">${employeeId}</td>
      </tr>
    `;
    }).join('');

    return `${baseMessage}
      <div style="max-height: 260px; overflow: auto; margin-top: 12px;">
        <table style="width:100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr>
              <th style="text-align:left; padding: 4px 8px;">name</th>
              <th style="text-align:left; padding: 4px 8px;">email</th>
              <th style="text-align:left; padding: 4px 8px; white-space: nowrap;">employee id</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  private getUserDisplayValue(user: IDirectoryUser | any, propertyNames: string[], fallback: string = 'unknown'): string {
    for (const propertyName of propertyNames) {
      const value = user?.[propertyName];
      if (value != null && `${value}`.trim() !== '') {
        return `${value}`;
      }
    }

    return fallback;
  }

  async retireAccounts(deleteUserRequest: DeleteUserRequest) {
    this.isSubmitting = true;

    const observer = {
      next: (x: IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>) => {
        this.submittedRequests = x.requests;
      },
      error: (err: any) => {
        this.submitErrorMessage.message = err.statusText;
        this.submitErrorMessage.errors = err?.error?.errors ?? [];
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    };

    let requestCall = this.userService.DeleteUsers(deleteUserRequest);
    requestCall.subscribe(observer);
  }

  //search user
  search() {
    this.userTypeString = this.userType?.toLowerCase();

    if (!this.userType) {
      this.errorMessage = 'Woops, looks like this component was used without providing a user type.';
      this.gettingUsers = false;
      return;
    }

    if (this.hasPermissions() || this.hasAdminRole) {
      this.selection.clear();

      const term: string = "";
      let stewardId: string | undefined = '';
      let enabledAccountsOnly: boolean = true;

      if (this.usingAdminRole) {
        enabledAccountsOnly = false;

        if (this.usersDataSourceAsSupport.data != null && this.usersDataSourceAsSupport.data.length > 0) {
          this.usersDataSource.data = this.usersDataSourceAsSupport.data;
          return;
        }

      } else {
        stewardId = this.userType === UserType.Coco ? undefined : this.authService.cvxClaimsPrincipal.objectId;

        if (this.usersDataSourceAsSteward.data != null && this.usersDataSourceAsSteward.data.length > 0) {
          this.usersDataSource.data = this.usersDataSourceAsSteward.data;
          return;
        }
      }

      // search is starting
      this.gettingUsers = true;

      //when enabledAccountsOnly=false, search all enabeld and disabled users
      this.userService.SearchUser(GraphConstants.DefaultGraphDomainString, term, this.userType, stewardId, ResultSizeConstants.GetAll, enabledAccountsOnly).subscribe({
        next: (r) => {
          if (this.usingAdminRole) {
            this.usersDataSourceAsSupport.data = r.data;
          } else {
            this.usersDataSourceAsSteward.data = r.data;
          }
          this.usersDataSource.data = r.data;
        },
        error: (e) => {
          console.error(e);
          this.errorMessage = `Encountered unexpected error while searching for ${this.userTypeString} accounts.`;
          this.gettingUsers = false;
        },
        complete: () => {
          this.errorMessage = this.usersDataSource.data.length === 0 ? `No ${this.userTypeString} accounts were found.` : "";
          this.gettingUsers = false;
        }
      });
    }
    else {
      this.errorMessage = `only authorized users can manage ${this.userTypeString} accounts.`;
      this.gettingUsers = false;
    }
  }

  handleRoleToggleChange(selectedOption: string) {

    this.usingAdminRole = selectedOption === this.adminRole;

    // start fresh
    this.selection.clear();
    this.filter('');

    this.search();

    if (this.usingAdminRole) {
      this.cols = ['select', ...this.displayColumns, 'stewards', 'accountEnabled'];
      this.selectedDisplayCols = [...this.displayColumns, 'stewards', 'accountEnabled'];
    } else {
      this.cols = ['select', ...this.displayColumns];
      this.selectedDisplayCols = [...this.displayColumns];
    }
  }

  handleToggleChange(selectedOption: string) {
    this.userType = UserType[selectedOption as keyof typeof UserType];

    // we need to clear the data sources, so that search will acutally happen (and not serve from cache)
    this.usersDataSourceAsSupport.data = [];
    this.usersDataSourceAsSteward.data = [];

    this.search();
  }

  //for checkbox select all use
  isAllSelected() {
    return matTableSelectionHelper.isAllSelected(this.selection, this.usersDataSource);
  }
  masterToggle() {
    return matTableSelectionHelper.toggleSelectAll(this.selection, this.usersDataSource);
  }

}